import { useState } from "react";
import NextVideoBlock from "@/components/shopwindow/NextVideoBlock";
import UpNextList from "@/components/shopwindow/UpNextList";
import {
  isChainActive,
  isChainAnchoredOnMethodStep,
} from "@/modules/chainReveal";
import chainPrompt from "@/modules/chainPrompt";
import CloseXIcon from "@svg/close_x.svg";
import { valueNames, shopWindowTypes } from "@/modules/Constants";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";
import type { ExperienceState } from "@/types/state";

type LandscapeChainPromptProps = {
  experienceState: ExperienceState;
  onApplicationEvent?: (event: Record<string, unknown>) => void;
};

/**
 * The "Up Next" popup for immersive landscape: a compact card over the video,
 * mounted once at the Experience panel level. It renders nothing until the chain
 * is revealed or the viewer reaches the final Method step. The full
 * recommendation list sits above a single inline NextVideoBlock, which owns the
 * countdown and auto-advance (never add a second inline block in landscape).
 */
export default function LandscapeChainPrompt({
  experienceState,
  onApplicationEvent,
}: LandscapeChainPromptProps) {
  // Only forces the re-render that drops the card when it is closed in this
  // mount. The durable dismissed flag lives in chainPrompt, so it survives the
  // remount that a rotate causes.
  const [allDismissedForId, setAllDismissedForId] = useState<unknown>(null);

  const currentId = () => {
    const experienceData = experienceState.experienceData;
    return experienceData ? experienceData.idexperience : null;
  };

  // Closing drops this mount, which also unmounts NextVideoBlock and clears its
  // countdown, so auto-advance stops.
  const onClose = () => {
    chainPrompt.dismiss(currentId());
    setAllDismissedForId(currentId());
  };

  const appData = experienceState.applicationData;
  const experienceData = experienceState.experienceData;
  const id = currentId();
  // Shows once the chain is revealed, or early on the final Method step.
  const shopWindowConfig = appData.getValue(valueNames.SHOP_WINDOW_CONFIG) as
    | ShopWindowConfig
    | null
    | undefined;
  const onLastMethodStep =
    shopWindowConfig &&
    shopWindowConfig.type === shopWindowTypes.METHOD &&
    isChainAnchoredOnMethodStep(appData, shopWindowConfig, experienceData);
  // The shared flag also counts: a Cancel in an earlier mount stays dismissed
  // after a rotate remounts this component.
  const dismissed = allDismissedForId === id || chainPrompt.isDismissed(id);
  if ((!isChainActive(appData) && !onLastMethodStep) || dismissed) {
    return null;
  }
  return (
    <div className="landscape-chain">
      <button
        type="button"
        className="landscape-chain__close"
        aria-label="Close recommendations"
        onClick={onClose}
      >
        <CloseXIcon className="centered icon-close-x-d" />
      </button>
      <UpNextList
        heading="You may also like"
        experienceState={experienceState}
        onApplicationEvent={onApplicationEvent}
      />
      <div className="landscape-chain__upnext">
        <NextVideoBlock
          variant="inline"
          compact
          lastStepPrompt
          experienceState={experienceState}
          onApplicationEvent={onApplicationEvent}
        />
      </div>
    </div>
  );
}
